/**
 * The Trend section's data source (REQ-STATUS-064..-073).
 *
 * One request per entity per window — never one per metric — and **only while
 * the section is expanded** (REQ-STATUS-072). The section is collapsible and its
 * collapsed state is Profile-captured (REQ-UI-131), so an operator who keeps it
 * shut, or a Profile that ships it shut, pays nothing for it.
 *
 * Cross-references:
 *   - REQ-STATUS-065 (the read), -066 (enumerated windows), -072 (bounded cost).
 *   - ``use-metric-catalog`` — the sibling hook this mirrors in shape.
 */
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api-client";
import { useSession } from "@/auth/session-context";
import { SYSTEM_STATUS_READER_ROLES } from "@/system-status/use-system-status-snapshot";
import { useLiveFrameRefresh } from "@/system-status/use-live-frame-refresh";
import type { components } from "@/lib/api-types.gen";

/** The server's history payload, straight from the generated schema. */
export type ComponentHistory = components["schemas"]["ComponentHistoryModel"];
/** One numeric series (gauge or counter). */
export type HistorySeries = components["schemas"]["SeriesModel"];

/** The window vocabulary as the server defines it, straight from the schema. */
type ServerHistoryWindow = components["schemas"]["HistoryWindow"];

/**
 * The windows the server enumerates (REQ-STATUS-066), in **display order** —
 * shortest first, which a schema enum does not carry.
 *
 * Capped at `7d` (architect, 2026-08-02 — feature-spec §9 decision 2): `30d` and
 * `90d` sat beyond the 14-day raw horizon, where the pane lost both its state
 * band and every domain metric and got *emptier* the further back you looked.
 *
 * The list is ordered here but **validated against the generated schema** in both
 * directions, so it cannot drift from the server:
 *
 *   - `satisfies` rejects an entry the server does not serve — a stale `"90d"`
 *     left behind here is a compile error, not a 422 an operator discovers.
 *   - {@link _everyServerWindowIsOffered} rejects the inverse, a window the
 *     server gained that the selector never offers — the silent half, since
 *     nothing at runtime would ever complain about a window simply missing from
 *     a dropdown.
 */
export const HISTORY_WINDOWS = [
  "1h",
  "6h",
  "24h",
  "7d",
] as const satisfies readonly ServerHistoryWindow[];

export type HistoryWindow = (typeof HISTORY_WINDOWS)[number];

/**
 * Compile-time proof that the selector offers EVERY window the server serves.
 *
 * {@link _UnofferedWindow} resolves to `never` while the two agree; a server-side
 * addition makes it that window, and `_AssertNever`'s constraint then fails with
 * the window named in the error.
 *
 * The constraint is the load-bearing part. The obvious spelling —
 * `const x: _UnofferedWindow[] = []` — **never fails**, because the empty array
 * is assignable to any array type; it compiles clean while catching nothing.
 */
type _AssertNever<T extends never> = T;
type _UnofferedWindow = Exclude<ServerHistoryWindow, HistoryWindow>;
export type _EveryServerWindowIsOffered = _AssertNever<_UnofferedWindow>;

/**
 * The default window.
 *
 * Six hours is long enough to show an overnight trend and short enough that the
 * state series (raw-only, ~14 d) is always available at it — so the default view
 * never opens on a pane explaining why something is missing.
 */
export const DEFAULT_HISTORY_WINDOW: HistoryWindow = "6h";

/**
 * Floor between live-frame-driven re-reads of a Trend series (REQ-STATUS-099).
 *
 * Deliberately an order of magnitude above :data:`SNAPSHOT_MIN_REFRESH_MS`: the
 * history is bucketed at a minute or coarser, so a faster re-read spends a
 * request to redraw an identical line. This is the rate the hooks' long-standing
 * ``staleTime`` already described — until now nothing ever triggered a re-read,
 * so that ceiling governed an event that never happened.
 */
export const HISTORY_MIN_REFRESH_MS = 60_000;

/** Human copy for the window selector and the accessible names (REQ-STATUS-070). */
export const HISTORY_WINDOW_LABELS: Record<HistoryWindow, string> = {
  "1h": "last hour",
  "6h": "last 6 hours",
  "24h": "last 24 hours",
  "7d": "last 7 days",
};

export const componentHistoryKey = (
  componentId: string,
  window: HistoryWindow,
): readonly unknown[] => ["system-status", "history", componentId, window] as const;

/** The TanStack Query key for one connection's history series. */
export const connectionHistoryKey = (
  componentId: string,
  connectionId: string,
  window: HistoryWindow,
): readonly unknown[] => ["system-status", "connection-history", componentId, connectionId, window];

/**
 * Fetch one component's bucketed history.
 *
 * ``enabled`` carries the whole of REQ-STATUS-072: the caller passes the
 * section's expanded state, so a collapsed Trend section issues **no** request.
 * Changing window issues one request, not one per series — the server returns
 * every series for the window in a single payload.
 */
export function useComponentHistory({
  componentId,
  window,
  enabled,
}: {
  componentId: string | null;
  window: HistoryWindow;
  enabled: boolean;
}): UseQueryResult<ComponentHistory, ApiError> {
  const session = useSession();
  const gated = enabled && componentId !== null && session.hasAnyRole(SYSTEM_STATUS_READER_ROLES);
  const query = useQuery<ComponentHistory, ApiError>({
    queryKey: componentHistoryKey(componentId ?? "", window),
    // Same four-role gate as the snapshot and the catalog — the history returns,
    // at earlier timestamps, fields those already return to the same roles.
    enabled: gated,
    queryFn: async ({ signal }) => {
      const path = `/api/system/components/${encodeURIComponent(
        componentId ?? "",
      )}/history?window=${window}`;
      return await apiFetch<ComponentHistory>(path, { signal });
    },
    // History is bucketed at a minute or coarser, so refetching on every focus
    // would spend a request to redraw an identical line. A minute is under the
    // finest bucket the server will serve.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // The BFF's RBAC verdict is authoritative; retrying a 403 repeats a denial.
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return false;
      return failureCount < 2;
    },
  });
  // The Trend line only moves when the collector writes a new bucket; the live
  // frame is the signal that it has (REQ-STATUS-099).
  useLiveFrameRefresh({
    queryKey: componentHistoryKey(componentId ?? "", window),
    refetch: query.refetch,
    minIntervalMs: HISTORY_MIN_REFRESH_MS,
    enabled: gated,
  });

  return query;
}

/**
 * One connection's throughput / error history (REQ-STATUS-071).
 *
 * Shares :type:`ComponentHistory` — the payload is the same contract, so the two
 * panes render through one component rather than two that must be kept in step.
 *
 * ⚠ A connection has **no history beyond raw retention**: `connection_samples`
 * has no continuous aggregate, so a long window is an *empty* view rather than a
 * thinner one. The server states that in `unavailable`, and the section renders
 * the reason rather than looking like a quiet connection.
 */
export function useConnectionHistory({
  componentId,
  connectionId,
  window,
  enabled,
}: {
  componentId: string | null;
  connectionId: string | null;
  window: HistoryWindow;
  enabled: boolean;
}): UseQueryResult<ComponentHistory, ApiError> {
  const session = useSession();
  const gated =
    enabled &&
    componentId !== null &&
    connectionId !== null &&
    session.hasAnyRole(SYSTEM_STATUS_READER_ROLES);
  const historyKey = connectionHistoryKey(componentId ?? "", connectionId ?? "", window);
  const query = useQuery<ComponentHistory, ApiError>({
    queryKey: historyKey,
    enabled: gated,
    queryFn: async ({ signal }) => {
      const path =
        `/api/system/components/${encodeURIComponent(componentId ?? "")}` +
        `/connections/${encodeURIComponent(connectionId ?? "")}/history?window=${window}`;
      return await apiFetch<ComponentHistory>(path, { signal });
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return false;
      return failureCount < 2;
    },
  });
  // The Trend line only moves when the collector writes a new bucket; the live
  // frame is the signal that it has (REQ-STATUS-099).
  useLiveFrameRefresh({
    queryKey: historyKey,
    refetch: query.refetch,
    minIntervalMs: HISTORY_MIN_REFRESH_MS,
    enabled: gated,
  });

  return query;
}
